import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getFileUrl } from '../utils/assetUrlHelper';

export default function HeroSlider({ banners = [] }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Default banners if none provided
  const slides = banners.length > 0 ? banners : [
    {
      id: 1,
      title: 'Welcome to ICAR-CISH',
      image_url: '/images/winter-school-banner.png',
      alt_text: 'ICAR-CISH Winter School Banner'
    },
  ];

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Auto-advance slides
  useEffect(() => {
    if (isPaused || slides.length <= 1) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [isPaused, slides.length, nextSlide]);

  return (
    <section 
      className="relative w-full overflow-hidden bg-gray-100"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="hero-slider"
    >
      {/* Slides container - responsive height */}
      <div className="relative w-full h-[200px] sm:h-[300px] md:h-[400px] lg:h-[480px]">
        {slides.map((slide, index) => (
          <div
            key={slide.id || index}
            className={`absolute inset-0 transition-opacity duration-500 ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
            data-testid={`slide-${index}`}
          >
            {/* Desktop Image (hidden on mobile if mobile_image_url exists) */}
            <img
              src={getFileUrl(slide.image_url)}
              alt={slide.alt_text || slide.title}
              className={`w-full h-full object-cover object-center ${
                slide.mobile_image_url ? 'hidden md:block' : ''
              }`}
              loading={index === 0 ? 'eager' : 'lazy'}
            />
            
            {/* Mobile Image (shown only on mobile if mobile_image_url exists) */}
            {slide.mobile_image_url && (
              <img
                src={getFileUrl(slide.mobile_image_url)}
                alt={slide.alt_text || slide.title}
                className="block md:hidden w-full h-full object-cover object-center"
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            )}
            
            {/* Overlay for text if needed */}
            {slide.link_url && (
              <a
                href={slide.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute inset-0 z-20"
                aria-label={`View ${slide.title}`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Navigation arrows - smaller on mobile */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-30 w-8 h-8 md:w-10 md:h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all"
            aria-label="Previous slide"
            data-testid="slider-prev"
          >
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-[#4A4A4A]" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-30 w-8 h-8 md:w-10 md:h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all"
            aria-label="Next slide"
            data-testid="slider-next"
          >
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-[#4A4A4A]" />
          </button>
        </>
      )}

      {/* Pagination dots - hidden on mobile, visible on desktop */}
      {slides.length > 1 && (
        <div className="hidden md:flex absolute bottom-4 left-1/2 -translate-x-1/2 z-30 gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                index === currentSlide
                  ? 'bg-[#0E6D2B] w-6'
                  : 'bg-white/70 hover:bg-white'
              }`}
              aria-label={`Go to slide ${index + 1}`}
              data-testid={`dot-${index}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
